import { Container } from "react-bootstrap";
import { CommunicationStatus } from "@/briefpoint-client";
import Loading from "@/components/Loading";
import { FormModal } from "@/components/Modals";
import AddEditClientTaskModal from "./AddEditClientTaskModal";
import ReviewClientTasksAddTaskControls from "./ReviewClientTasksAddTaskControls";
import ReviewClientTasksAssignedList from "./ReviewClientTasksAssignedList";
import ReviewClientTasksBreadcrumbsAndHeading from "./ReviewClientTasksBreadcrumbsAndHeading";
import ReviewClientTasksBulkSimplifyProgress from "./ReviewClientTasksBulkSimplifyProgress";
import ReviewClientTasksClientLanguageSection from "./ReviewClientTasksClientLanguageSection";
import panelStyles from "./ReviewClientTasksContentPanel.module.scss";
import ReviewClientTasksEmptyState from "./ReviewClientTasksEmptyState";
import ReviewClientTasksLockedBanner from "./ReviewClientTasksLockedBanner";
import ReviewClientTasksNextRow from "./ReviewClientTasksNextRow";
import ReviewClientTasksUnassignedList from "./ReviewClientTasksUnassignedList";
import { useReviewClientTasksPageActions } from "./useReviewClientTasksPageActions";
import { useReviewClientTasksPageData } from "./useReviewClientTasksPageData";
import { useReviewClientTasksSimplifyAndLanguage } from "./useReviewClientTasksSimplifyAndLanguage";

export default function ReviewClientTasks() {
  const pageData = useReviewClientTasksPageData();
  const simplifyAndLanguage = useReviewClientTasksSimplifyAndLanguage(pageData);
  const pageActions = useReviewClientTasksPageActions(pageData);

  const isUnassignedSectionVisible =
    simplifyAndLanguage.isReviewTasksMainContentVisible &&
    pageData.unassignedPrimaryTasks.length > 0;

  const isNextRowElevated =
    pageData.isReviewLocked &&
    pageData.activeCommunication?.status === CommunicationStatus.Pending;

  const showNextButtonRow =
    pageData.hasClientTasks &&
    simplifyAndLanguage.isReviewTasksMainContentVisible;

  return (
    <Container className="page-container">
      <AddEditClientTaskModal
        interrogatories={pageData.documentQuery.data?.interrogatories ?? []}
        communicationId={pageData.activeCommunication?.id ?? ""}
        externalCommunication={pageActions.editCommunication}
        language={simplifyAndLanguage.language}
      />
      <FormModal
        cancelText="No, go back"
        confirmText="Yes, delete"
        show={!!pageActions.deleteCommunication}
        onClose={() => pageActions.setDeleteCommunication(undefined)}
        onConfirm={() => {
          pageActions.deleteCommunicationItem(undefined);
        }}
        cancelButtonVariant="outline-primary"
        title="Are you sure you want to delete?"
      >
        <div>
          This task is linked to one or more requests. Deleting it will
          permanently remove those associations.
        </div>
      </FormModal>
      <Loading isLoading={pageData.isPageInitialDataLoading}>
        <ReviewClientTasksBreadcrumbsAndHeading
          documentId={pageData.documentId}
          document={pageData.documentQuery.data}
        />
        {pageData.isReviewLocked ? (
          <ReviewClientTasksLockedBanner
            width={simplifyAndLanguage.reviewTasksPanelWidth}
          />
        ) : null}
        <div
          data-testid="review-client-tasks-document-name"
          className="fw-bold fs-4"
        >
          {pageData.documentQuery.data?.name}
        </div>
        <ReviewClientTasksClientLanguageSection
          language={simplifyAndLanguage.language}
          isLanguageControlsDisabled={
            simplifyAndLanguage.isLanguageControlsDisabled
          }
          onSelectEnglish={simplifyAndLanguage.onSelectEnglish}
          onSelectSpanish={simplifyAndLanguage.onSelectSpanish}
        />
        <div
          className={panelStyles.reviewTasksPanel}
          style={{ width: simplifyAndLanguage.reviewTasksPanelWidth }}
        >
          {simplifyAndLanguage.isSimplifyAllProgressPanelVisible ? (
            <ReviewClientTasksBulkSimplifyProgress
              percent={simplifyAndLanguage.bulkSimplifyProgressPercent}
              label={simplifyAndLanguage.bulkSimplifyProgressLabel}
            />
          ) : null}
          <div>
            {simplifyAndLanguage.isEmptyClientTasksState ? (
              <ReviewClientTasksEmptyState
                simplifyAndLanguage={simplifyAndLanguage}
              />
            ) : null}
            {simplifyAndLanguage.isReviewTasksMainContentVisible ? (
              <ReviewClientTasksAssignedList
                documentId={pageData.documentId}
                language={simplifyAndLanguage.language}
                activeCommunication={pageData.activeCommunication}
                interrogatories={
                  pageData.documentQuery.data?.interrogatories ?? []
                }
                primaryTasksSorted={pageData.primaryTasksSorted}
                onDeleteInterrogatory={pageActions.deleteInterrogatory}
                onRequestDeleteWithModal={pageActions.requestDeleteWithModal}
                onOpenTaskEditor={pageActions.openTaskEditor}
              />
            ) : null}
            {pageData.hasClientTasks &&
            simplifyAndLanguage.isReviewTasksMainContentVisible ? (
              <ReviewClientTasksAddTaskControls
                isSimplifyAllRemainingButtonVisible={
                  pageData.isSimplifyAllRemainingButtonVisible
                }
                isReviewLocked={pageData.isReviewLocked}
                isBulkSimplifyJobBusy={
                  simplifyAndLanguage.isBulkSimplifyJobBusy
                }
                isSimplifyAllBulkActionsDisabled={
                  simplifyAndLanguage.isSimplifyAllBulkActionsDisabled
                }
                bulkSimplifyLoadingSource={
                  simplifyAndLanguage.bulkSimplifyLoadingSource
                }
                onAddTask={pageActions.addTask}
                onCreateSimplifiedRemaining={() => {
                  simplifyAndLanguage.runCreateCommunicationThenBulkSimplify(
                    "remaining",
                  );
                }}
              />
            ) : null}
            {isUnassignedSectionVisible ? (
              <ReviewClientTasksUnassignedList
                documentId={pageData.documentId}
                language={simplifyAndLanguage.language}
                interrogatories={
                  pageData.documentQuery.data?.interrogatories ?? []
                }
                items={pageData.unassignedPrimaryTasks}
                onDeleteItem={pageActions.deleteCommunicationItem}
                onOpenTaskEditor={pageActions.openTaskEditor}
              />
            ) : null}
          </div>
          {showNextButtonRow ? (
            <ReviewClientTasksNextRow
              isNextRowElevated={isNextRowElevated}
              isNextStepBlocked={pageData.isNextStepBlocked}
              isBulkSimplifyJobBusy={simplifyAndLanguage.isBulkSimplifyJobBusy}
              isBulkTranslationWorkInProgress={
                simplifyAndLanguage.isBulkTranslationWorkInProgress
              }
              onNext={pageActions.saveAndRedirectToReviewFlow}
            />
          ) : null}
          {pageData.isReviewLocked ? (
            <div
              className={panelStyles.reviewTasksOverlay}
              aria-hidden
              data-testid="review-client-tasks-sent-overlay"
            />
          ) : null}
        </div>
      </Loading>
    </Container>
  );
}
